import { useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import { Datagrid, FunctionField } from "react-admin";
import { InvoiceStateBadge } from "../fields/InvoiceStateField";
import { MoneyField } from "../fields/MoneyField";
import { Invoice } from "../providers/invoicesProvider";
import type { Student } from "../providers/studentsProvider.js";
import { formatDate, formatTime } from "../utils/calendar";
import { InvoicePosition, type PaymentPaymentMethodsEnum } from "../generated/backendClient";
import { StudentInvoiceDialog } from "../dialogs/StudentInvoiceDialog";
import { TextTrimmer } from "../misc/TextTrimmer";
import { ManualPrepaidPaymentButton } from "../buttons/ManualPrepaidPaymentButton";
import { useDialog } from "../hooks/useDialog";
import { EmptyState } from "../misc/EmptyState";

export function InvoicesList({ student }: { student: Student }) {
  // All columns are marked as not sortable, because sort order must always be: createdAt DESC ...

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");
  const [cancelNextClickOnResource, setCancelNextClickOnResource] = useState<string | null>(null);
  const { dialogProps, openDialog, closeDialog } = useDialog();

  const getLastInvoiceChars = (num: string) => {
    return num.slice(-5);
  };

  const copyInvoiceNumberToClipboard = (invoiceNumber: string) => {
    navigator.clipboard
      .writeText(invoiceNumber)
      .then(() => {
        showSuccessToast("Vollständige Rechnungsnummer in die Zwischenablage kopiert.");
      })
      .catch(() => {
        showErrorToast("Fehler beim Kopieren der Rechnungsnummer in die Zwischenablage.");
      });
  };

  const prettyPaymentMethod = (method: PaymentPaymentMethodsEnum) => {
    switch (method) {
      case "acss_debit":
        return "ACSS Debit";
      case "affirm":
        return "Affirm";
      case "afterpay_clearpay":
        return "Afterpay/Clearpay";
      case "alipay":
        return "Alipay";
      case "au_becs_debit":
        return "AU BECS Debit";
      case "bacs_debit":
        return "BACS Debit";
      case "bancontact":
        return "Bancontact";
      case "blik":
        return "BLIK";
      case "boleto":
        return "Boleto";
      case "card":
        return "Kreditkarte";
      case "card_present":
        return "Kartenterminal";
      case "customer_balance":
        return "Banktransfer"; //
      case "eps":
        return "EPS";
      case "fpx":
        return "FPX";
      case "giropay":
        return "Giropay";
      case "grabpay":
        return "GrabPay";
      case "ideal":
        return "iDEAL";
      case "interac_present":
        return "Interac";
      case "klarna":
        return "Klarna";
      case "konbini":
        return "Konbini";
      case "link":
        return "Link";
      case "oxxo":
        return "OXXO";
      case "p24":
        return "P24";
      case "paynow":
        return "PayNow";
      case "pix":
        return "Pix";
      case "promptpay":
        return "PromptPay";
      case "sepa_debit":
        return "SEPA Lastschrift";
      case "sofort":
        return "Sofort";
      case "us_bank_account":
        return "US Bank Account";
      case "wechat_pay":
        return "WeChat Pay";
      case "prepaid_credits":
        return "Guthaben";
    }
  };

  const isManualPrepaidPaymentInvoice = (invoice: Invoice) => {
    return (
      invoice.payload.state === "UNPAID" &&
      invoice.payload.payment_mode === "PREPAID_CREDITS_ONLY" &&
      invoice.payload.could_be_paid_from_users_prepaid_credits
    );
  };

  const invoiceRowClick = (id: string) => {
    if (student.featureToggles?.canViewInvoiceDetails) {
      setSelectedInvoiceId(id);
      openDialog();
      return false;
    } else {
      showErrorToast("Detailansicht nicht verfügbar für Datapart Kunden.");
    }
  };

  const closeInvoiceDialog = () => {
    closeDialog();
    setSelectedInvoiceId("");
  };

  return (
    <>
      <Datagrid
        bulkActionButtons={false}
        empty={<EmptyState noEntriesText="Keine Rechnungen im neuen Rechnungssystem vorhanden." />}
        rowClick={(id) => {
          if (cancelNextClickOnResource === id.toString()) {
            // workaround to prevent the rowClick from being triggered when clicking on the "Bezahlen" button in the "Status" column
            setCancelNextClickOnResource(null); // reset the cancelNextClickOnResource state after the click has been cancelled
            return false;
          }
          invoiceRowClick(id.toString());
          return false;
        }}
      >
        <FunctionField
          render={(invoice: Invoice) => {
            return (
              <Tooltip title={invoice.nr}>
                <Box
                  sx={{ display: "flex", cursor: "pointer" }}
                  onClick={() => copyInvoiceNumberToClipboard(invoice.nr)}
                >
                  <Box
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "62px",
                    }}
                  >
                    {invoice.nr}
                  </Box>
                  {getLastInvoiceChars(invoice.nr)}
                </Box>
              </Tooltip>
            );
          }}
          label="Rechnungs-Nr."
          sortable={false}
          noWrap
        />
        <FunctionField
          render={(invoice: Invoice) => {
            return (
              <>
                {invoice.payload.positions.map((p) => (
                  <div key={p.id}>{<TextTrimmer text={getInvoiceBrief(p)} maxLength={25} />}</div>
                ))}
              </>
            );
          }}
          label="Leistungen"
          sortable={false}
        />
        <FunctionField
          label="Leistungs&shy;zeitpunkt"
          sortable={false}
          render={(invoice: Invoice) => {
            const { date, time } = getInvoiceDateAndTime(invoice);

            return (
              <>
                <Box>{date}</Box>
                <Box>{time} Uhr</Box>
              </>
            );
          }}
        />
        <MoneyField label="Betrag" source="total" textAlign="right" sortable={false} />
        <FunctionField
          label="Bezahlt mit"
          source="payment"
          render={(invoice: Invoice) => {
            const paymentMethods = invoice.payload.payment?.payment_methods || [];
            return (
              <Tooltip title={paymentMethods.map(prettyPaymentMethod).join(", ")}>
                <span style={{ textOverflow: "ellipsis", width: "100px", display: "inline-block", overflow: "hidden" }}>
                  {paymentMethods.map(prettyPaymentMethod).join(", ")}
                </span>
              </Tooltip>
            );
          }}
        />
        <FunctionField
          label="Status"
          source="status"
          render={(invoice: Invoice) => {
            // companies using EXTERNAL_BANK_TRANSFER as payment mode will have
            // PREPAID_CREDITS_ONLY invoices that are UNPAID. If they could be paid
            // from prepaid credits, we want to show a payment button
            if (isManualPrepaidPaymentInvoice(invoice)) {
              return (
                <ManualPrepaidPaymentButton
                  invoiceId={invoice.id}
                  paymentAmount={invoice.total}
                  onClickCallback={() => setCancelNextClickOnResource(invoice.id)}
                />
              );
            }
            return <InvoiceStateBadge invoice={invoice.payload} />;
          }}
        />
      </Datagrid>
      {selectedInvoiceId && (
        <StudentInvoiceDialog
          student={student}
          invoiceId={selectedInvoiceId}
          dialogProps={{ ...dialogProps, onClose: closeInvoiceDialog }}
        />
      )}
    </>
  );
}

export function getInvoiceBrief(position: InvoicePosition): string {
  const { driving_lesson_type, driving_license_class, ue_count, description } = position;

  if (!driving_lesson_type || !driving_license_class || !ue_count) {
    return description;
  }

  const parsedCount = Math.round(parseFloat(ue_count));
  return `${driving_license_class}: ${parsedCount} x ${driving_lesson_type}`;
}

export function getInvoiceDateAndTime(invoice: Invoice): { date: string; time: string } {
  const dateTimeArray = invoice.payload.positions.map((p) =>
    p.service_at
      ? {
          date: formatDate(DateTime.fromISO(p.service_at)),
          time: formatTime(DateTime.fromISO(p.service_at)),
        }
      : null,
  );
  const filteredDateTimeArray = dateTimeArray.filter((it) => it !== null)?.[0];
  const { date, time } = filteredDateTimeArray as { date: string; time: string };
  return { date, time };
}
